import type { SavedView, SavedViewPatchInput, SavedViewWriteInput, ViewClient } from '../client';
import type { Transport } from './transport';

/**
 * `ViewClient` over `/api/views` (record-page-and-views.md D14-D19).
 *
 * The thinnest client in the app, and deliberately so: the SHAPE of a legal view is
 * decided once, on the server, by `views/validate.ts` - which refuses a RELATION in
 * `order_by`, more than four sort keys, an operator illegal for its field's type and a
 * filter that would not survive a jsonb round trip. Re-checking any of that here would
 * be a second validator (INV-6) that drifts from the first the day a field type is
 * added.
 *
 * WHAT THIS CLIENT DOES NOT DO IS MATERIALISE. A stored filter is a `ViewFilterNode`,
 * which may carry a relative-date token; `materialiseViewFilter(node, new Date())` runs
 * where the view is USED, in the browser, at read time. That is open risk 6's v1 answer:
 * the server is UTC and Ben is in Germany, and the person reading "due this week" is the
 * person whose week it is. A client that resolved tokens on the way in would freeze
 * Monday's answer into a view that is supposed to still mean something next month.
 */
export class HttpViewClient implements ViewClient {
  constructor(private readonly transport: Transport) {}

  async list(objectId?: string): Promise<readonly SavedView[]> {
    const response = await this.transport.request<{ views: readonly SavedView[] }>({
      method: 'GET',
      path: '/api/views',
      ...(objectId === undefined ? {} : { query: { objectId } }),
    });
    return response.views;
  }

  async create(input: SavedViewWriteInput): Promise<SavedView> {
    const response = await this.transport.request<{ view: SavedView }>({
      method: 'POST',
      path: '/api/views',
      body: input,
    });
    return response.view;
  }

  async update(viewId: string, patch: SavedViewPatchInput): Promise<SavedView> {
    const response = await this.transport.request<{ view: SavedView }>({
      method: 'PATCH',
      path: `/api/views/${viewId}`,
      body: patch,
    });
    return response.view;
  }

  async remove(viewId: string): Promise<void> {
    // 204, so there is nothing to read back. The soft delete frees both the name and
    // the default slot, which is what makes "delete, then save under the same name" work.
    await this.transport.request<void>({ method: 'DELETE', path: `/api/views/${viewId}` });
  }
}
